import { useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import defaultAvatar from '../images/default.jpg';
import { useStealthMode } from '../context/StealthModeContext';
import stealthImage from '../images/stealth-hide.png'

export default function InboxDrawer({ user, onClose }) {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  const { enabled } = useStealthMode();

  // cache user lookups so we don't refetch the same profile repeatedly
  const profileCache = useRef(new Map());

  useEffect(() => {
    if (!user) return;

    let myBlocked = []; // array to hold current user's blocked IDs

    // First fetch the current user's blocked list
    const fetchMyBlocked = async () => {
      try {
        const uq = query(collection(db, 'users'), where('authUID', '==', user.uid));
        const usnap = await getDocs(uq);
        if (!usnap.empty) {
          const udata = usnap.docs[0].data();
          myBlocked = Array.isArray(udata.blocked) ? udata.blocked : [];
        }
      } catch (e) {
        console.error("Error fetching my blocked list:", e);
      }
    };

    const q = query(
      collection(db, 'messages'),
      where('participants', 'array-contains', user.uid),
      orderBy('updatedAt', 'desc')
    );



    const unsub = onSnapshot(q, async (snap) => {
      await fetchMyBlocked(); // Ensure blocked array is up to date

      // Build rows with participant info
      const items = await Promise.all(
        snap.docs.map(async (d) => {
          const data = d.data();
          const [a, b] = data.participants || [];
          const otherUid = a === user.uid ? b : a;

          // default fallback
          let other = { displayName: otherUid || '', photoURL: '' };

          if (otherUid) {
            // try cache first
            let cached = profileCache.current.get(otherUid);
            if (!cached) {
              // users are stored with random doc IDs and authUID field; fetch by authUID
              const uq = query(collection(db, 'users'), where('authUID', '==', otherUid));
              const usnap = await getDocs(uq);
              if (!usnap.empty) {
                const udata = usnap.docs[0].data();
                cached = {
                  displayName: udata.displayName || otherUid,
                  // photos are saved in "displayPhoto" on registration
                  photoURL: udata.displayPhoto || udata.photoURL || '',
                  blocked: Array.isArray(udata.blocked) ? udata.blocked : []
                };
              } else {
                cached = { displayName: otherUid, photoURL: '', blocked: [] };
              }
              profileCache.current.set(otherUid, cached);
            }
            other = cached;
          }

          return { id: d.id, otherUid, other, ...data };
        })
      );

      // Filter out conversations where either user blocked the other
      const filteredItems = items.filter(item => {
        if (!item.otherUid) return true;
        const iBlockedThem = myBlocked.includes(item.otherUid);
        const theyBlockedMe = item.other?.blocked?.includes(user.uid);
        return !iBlockedThem && !theyBlockedMe;
      });

      setRows(filteredItems);
    });

    return unsub;
  }, [user]);

  const openConversation = (mid) => {
    navigate(`/message/${mid}`);
    // close the drawer right away
    if (typeof onClose === 'function') onClose();
  };




  return (
    <aside className="drawer secondary-drawer" aria-label="Messages">
      <div className="drawer-header">
        <button className="icon-btn" onClick={onClose} aria-label="Close messages">‹</button>
        <h3>Messages</h3>
      </div>

      <ul className="convo-list">
        {rows.length === 0 && <li className="empty">No conversations yet</li>}
        {rows.map((row) => (
          <li
            key={row.id}
            className={`convo ${row.updatedAt &&
              row.lastRead?.[user.uid] &&
              row.updatedAt.toMillis() <= row.lastRead[user.uid].toMillis()
              ? ''
              : 'unread'
              }`}
            onClick={() => openConversation(row.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openConversation(row.id);
              }
            }}
            style={{
              borderRadius: '10px'
            }}
            role="button"
            tabIndex={0}
          >

            <div className="user-meta">
              <img
                    className="avatar"
                    src={enabled ?stealthImage :row.other.photoURL || defaultAvatar}
                    style={{
                      cursor: 'pointer',
                      borderRadius: '50%',
                      height: enabled ? '25px' : '40px',
                      width: enabled ? '25px' : '40px',
                      padding: enabled ? '7.5px' : '0px',
                      objectFit: 'contain',
                      backgroundColor: '#24252A',

                    }}
                    alt="User avatar"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = defaultAvatar;
                    }}
                  />
              {/* {
                enabled ? <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '30px',
                  width: '30px',
                  backgroundColor: '#24252A',
                  borderRadius: '50%',
                }}>
                  <img src={stealthImage} style={{
                    borderRadius: '50%',
                    height: '15px',
                    width: '15px',
                    objectFit: 'cover',
                  }} />
                </div> :
                  <img
                    className="avatar"
                    src={row.other.photoURL || defaultAvatar}
                    style={{
                      borderRadius: '50%',

                    }}
                    alt="User avatar"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = defaultAvatar;
                    }}
                  />
              } */}

              <div className="inner-meta">
                <div className="name">{row.other.displayName}</div>
                <time className="when">
                  {row.updatedAt?.toDate?.()?.toLocaleString?.() || ''}
                </time>
              </div>
            </div>

            <div
              className="preview"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: 'flex-start',
                gap: "6px",
                width: "100%",
              }}
            >
              <div
                className="preview-text"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  // flex: 1,
                }}
              >
                {row.lastText
                  ? (() => {
                    const words = row.lastText.trim().split(/\s+/);
                    return (
                      words.slice(0, 2).join(" ") +
                      (words.length > 2 ? "..." : "")
                    );
                  })()
                  : "Start the conversation"}
              </div>

              {(!row.lastRead?.[user.uid] ||
                (row.updatedAt &&
                  row.lastRead?.[user.uid] &&
                  row.updatedAt.toMillis() > row.lastRead[user.uid].toMillis())) && (
                  <span
                    className="unread-dot"
                    aria-label="unread"
                    style={{
                      flexShrink: 0,
                      fontSize: "18px",
                      lineHeight: 1,
                    }}
                  >
                    •
                  </span>
                )}
            </div>

            {/* <div className="preview">
              <div className="preview-text">
                {row.lastText ? (() => {
                  const words = row.lastText.trim().split(/\s+/);
                  return words.slice(0, 2).join(" ") + (words.length > 2 ? "..." : "");
                })() : "Start the conversation"}
              </div>

          
              {(!row.lastRead?.[user.uid] ||
                (row.updatedAt &&
                  row.lastRead?.[user.uid] &&
                  row.updatedAt.toMillis() > row.lastRead[user.uid].toMillis())) && (
                  <span className="unread-dot" aria-label="unread">•</span>
                )}
            </div> */}

          </li>
        ))}
      </ul>
    </aside>
  );
}

